/**
 * Intégration du fingerprint dans les formulaires d'authentification
 * Wrapper qui ajoute automatiquement le fingerprint aux requêtes login/register
 */

import { getFingerprint } from './fingerprint.js';

/**
 * Ajoute le fingerprint à un FormData avant envoi
 * @param {FormData} formData Le FormData à enrichir
 * @returns {Promise<FormData>} Le FormData avec le fingerprint ajouté
 */
export async function addFingerprintToFormData(formData) {
    try {
        const fingerprint = await getFingerprint();
        formData.append('fingerprint', fingerprint);
        return formData;
    } catch (error) {
        console.error('Failed to add fingerprint:', error);
        // Continue sans fingerprint si erreur
        return formData;
    }
}

/**
 * Wrapper pour fetch qui ajoute automatiquement le fingerprint
 * @param {string} url URL de la requête
 * @param {Object} options Options fetch
 * @returns {Promise<Response>} Promesse de la réponse
 */
export async function fetchWithFingerprint(url, options = {}) {
    try {
        // Si c'est un FormData, ajouter le fingerprint
        if (options.body instanceof FormData) {
            options.body = await addFingerprintToFormData(options.body);
        }
        // Si c'est du JSON, ajouter le fingerprint
        else if (options.headers?.['Content-Type'] === 'application/json') {
            const bodyData = JSON.parse(options.body || '{}');
            const fingerprint = await getFingerprint();
            bodyData.fingerprint = fingerprint;
            options.body = JSON.stringify(bodyData);
        }

        return fetch(url, options);
    } catch (error) {
        console.error('Failed to fetch with fingerprint:', error);
        // Continue sans fingerprint si erreur
        return fetch(url, options);
    }
}

/**
 * Intercepte les soumissions de formulaires pour ajouter le fingerprint
 * @param {HTMLFormElement} form Le formulaire à intercepter
 */
export async function interceptFormSubmit(form) {
    if (!form) return;

    // Trouver le handler submit existant
    const originalSubmit = form.onsubmit;

    form.addEventListener('submit', async function(e) {
        // Vérifier si le formulaire contient déjà un champ fingerprint
        const existingFingerprint = form.querySelector('[name="fingerprint"]');
        if (!existingFingerprint) {
            try {
                const fingerprint = await getFingerprint();

                // Créer un input caché pour le fingerprint
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'fingerprint';
                input.value = fingerprint;
                form.appendChild(input);
            } catch (error) {
                console.error('Failed to add fingerprint to form:', error);
                // Continue sans fingerprint
            }
        }
    }, { capture: true }); // Utiliser capture pour s'exécuter avant les autres handlers
}

/**
 * Initialise l'intercepteur de fingerprint pour les formulaires d'auth
 * À appeler au chargement de la page
 */
export function initAuthFingerprinting() {
    // Intercepter le formulaire de login
    const loginForm = document.getElementById('login-form');
    if (loginForm) {
        interceptFormSubmit(loginForm);
    }

    // Intercepter le formulaire de register
    const registerForm = document.getElementById('register-form');
    if (registerForm) {
        interceptFormSubmit(registerForm);
    }

    // Intercepter le formulaire de création de guest
    const guestForm = document.getElementById('guest-form');
    if (guestForm) {
        interceptFormSubmit(guestForm);
    }

    console.log('Auth fingerprinting initialized');
}

// Auto-initialisation si le DOM est déjà chargé
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAuthFingerprinting);
} else {
    initAuthFingerprinting();
}
